import React, { useEffect, useState } from "react";
import { Alert, Button, Modal, Select, Spin } from "antd";
import { useAccountDetails } from "app/providers/AccountDetailsProvider";
import { useAgentAccountDetails } from "shared/hooks/common";
import { cookieExpiresFromToken, tokenManager } from "services/client/token-manager";
import { getUserOtherAccountsDetails, generateNewUserToken } from "../api/user-api";
import { GET_MAPPED_HUB_NAME } from "shared/constants/Mappings";
import { capitalizeFirstLetterEachWord } from "controllers/common";
import { useNavigate } from "react-router-dom";

export default function SwitchRoleHubPopup({ open = false, onClose = () => {} }) {
  const { refreshAccountDetails } = useAccountDetails();
  const { agentAccountId } = useAgentAccountDetails();
  const primaryAccountId = tokenManager.getPrimaryAccountId();
  const [roleOptions, setRoleOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState(agentAccountId);
  const [hubMapping, setHubMapping] = useState({});
  const [roleMapping, setRoleMapping] = useState({});
  const [alert, setAlert] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  /** Full-screen style hold message while account details refresh and before reload */
  const [isCompletingSwitch, setIsCompletingSwitch] = useState(false);

  useEffect(() => {
    if (!open) return;
    handleUserSecondaryDetails(primaryAccountId);
    setAlert("");
    setIsCompletingSwitch(false);
  }, [open]);
  const handleUserSecondaryDetails = async (id) => {
    try {
      const response = await getUserOtherAccountsDetails(id);
      const data = normalizedData(response?.results);
      setRoleOptions(data);
      const hubMapping = response?.results?.reduce((acc, obj) => {
        acc[obj?.account_id] = { name: obj?.hub_name, id: obj?.hub_id };
        return acc;
      }, {});
      const roleMapping = response?.results?.reduce((acc, obj) => {
        acc[obj?.account_id] = { name: obj?.role_name, id: obj?.role_id };
        return acc;
      }, {});
      setHubMapping(hubMapping);
      setRoleMapping(roleMapping);
    } catch (error) {
      console.error("error in fetching user secondary details", error);
      setRoleOptions([]);
      return [];
    }
  };
  const normalizedData = (data) => {
    if (!data || !Array.isArray(data)) {
      return [];
    }

    let newData = data?.map((obj) => {
      let normalizeName =
        capitalizeFirstLetterEachWord(obj?.role_name?.replaceAll("_", " ")) +
        " " +
        GET_MAPPED_HUB_NAME(obj?.hub_name);
      let item = {
        value: obj?.account_id,
        label: normalizeName,
        // disabled: agentAccountId === obj?.account_id,
        title: agentAccountId === obj?.account_id ? "Current User " : "",
      };
      return item;
    });
    return newData;
  };

  const handleApply = async () => {
    const data = {
      roleId: roleMapping[selectedOption]?.id,
      userId: selectedOption,
      hubId: hubMapping[selectedOption]?.id,
    };
    try {
      setLoading(true);
      const response = await generateNewUserToken(data);
      let tokens = response?.data?.data;
      const { accessToken, refreshToken } = tokens;
      if (accessToken && refreshToken) {
        tokenManager.setAccess(accessToken);
        tokenManager.setRefresh(refreshToken);
        tokenManager.setAuth(accessToken);
        onClose();
        setLoading(false);
        setIsCompletingSwitch(true);
        const expires = cookieExpiresFromToken(refreshToken);
        const detailsResult = await refreshAccountDetails({ expires });
        if (!detailsResult.ok) {
          setIsCompletingSwitch(false);
          setAlert(detailsResult.message || "Failed to refresh account details. Please try again.");
          return;
        }

        setTimeout(() => {
          // window.location.reload();
          // navigate("/");
          // window.location.assign("/");
          window.location.href = "/";
        }, 500);
      }
    } catch (error) {
      console.error("error in generating new user token", error);
      setAlert("Failed to switch role. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  return (
    <>
      <Modal
        open={isCompletingSwitch}
        closable={false}
        maskClosable={false}
        footer={null}
        centered
        width={420}
        zIndex={1100}
        styles={{ mask: { zIndex: 1099 } }}
      >
        <div
          className="flex flex-col items-center justify-center gap-5 py-6 px-4 text-center"
          style={{ minHeight: 200 }}
        >
          <Spin size="large" />
          <div>
            <p className="text-base font-semibold text-[#1f1f1f] m-0 mb-2">
              Switching your workspace
            </p>
            <p className="text-sm text-[#6e6e6e] m-0 leading-relaxed">
              Hang tight — we&apos;re updating your account and refreshing your session. This only
              takes a moment.
            </p>
          </div>
        </div>
      </Modal>

      <Modal
        open={open}
        mask
        centered
        width={420}
        title="Switch User"
        onCancel={() => {
          if (!isCompletingSwitch) onClose();
        }}
        footer={null}
      >
        <div className="flex flex-col gap-3">
          {alert ? (
            <Alert message={alert} type="error" showIcon closable onClose={() => setAlert("")} />
          ) : null}

          <div className="w-full">
            <h3 className="tag-header-dm-400 mb-[2px]">User*</h3>
            <Select
              className="w-full"
              options={roleOptions}
              value={selectedOption}
              placeholder="Select role"
              notFoundContent="No Role Found"
              onChange={(value) => {
                setAlert("");
                setSelectedOption(value);
              }}
            />
          </div>

          <p className="text-xs text-[#6e6e6e]">
            <strong>Role</strong>:{" "}
            {capitalizeFirstLetterEachWord(roleMapping[selectedOption]?.name?.replaceAll("_", " "))}{" "}
            <strong>Hub</strong>: {GET_MAPPED_HUB_NAME(hubMapping[selectedOption]?.name)}
          </p>

          <p className="text-xs text-[#8c8c8c] m-0 leading-relaxed">
            Note: If your workspace or role doesn&apos;t look updated after switching, try
            refreshing the page —{" "}
            <kbd className="px-1 py-0.5 rounded bg-[#f0f0f0] text-[#444] font-mono text-[11px]">
              ⌘
            </kbd>
            {" + "}
            <kbd className="px-1 py-0.5 rounded bg-[#f0f0f0] text-[#444] font-mono text-[11px]">
              R
            </kbd>{" "}
            on Mac, or{" "}
            <kbd className="px-1 py-0.5 rounded bg-[#f0f0f0] text-[#444] font-mono text-[11px]">
              Ctrl
            </kbd>
            {" + "}
            <kbd className="px-1 py-0.5 rounded bg-[#f0f0f0] text-[#444] font-mono text-[11px]">
              R
            </kbd>{" "}
            on Windows.
          </p>

          <div className="flex justify-end items-center gap-3 mt-1">
            <Button onClick={onClose}>Cancel</Button>
            <Button
              loading={loading}
              disabled={selectedOption === agentAccountId}
              type="primary"
              onClick={handleApply}
              style={{
                ...(selectedOption === agentAccountId
                  ? { cursor: "not-allowed", opacity: 0.65 }
                  : {}),
              }}
            >
              Apply
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
